import { SignRequest } from '@bitte-ai/types';
import { getAddress, maxUint256 } from 'viem';

const APPROVAL_SIGNATURE = '0x095ea7b3';
const MESSAGE_METHODS = ['eth_sign', 'personal_sign'];

export interface ApprovalInfo {
  spenderAddress: string;
  unlimited: boolean;
  tokenAddress: string;
  suspicious: boolean;
}

function isApproval({ method, params }: SignRequest): ApprovalInfo | undefined {
  // TODO: include ERC20Permit SignRequests in this classification.
  if (method !== 'eth_sendTransaction') return undefined;
  if (params.length > 1) {
    // This is a batch transction (may contain an approval but isn't one.)
    return undefined;
  }
  if (params.length === 0) {
    // This should never happen.
    throw new Error(
      'Invalid eth_sendTransaction request with no transactions!',
    );
  }
  const { to, value, data } = params[0]!; // Existence guaranteed by above conditions.
  if (!data.toLowerCase().startsWith(APPROVAL_SIGNATURE)) return undefined;
  if (data.length < 138) return undefined;
  return {
    spenderAddress: getAddress('0x' + data.slice(34, 74)),
    // Anything more than half of  2^256
    unlimited: BigInt('0x' + data.slice(74, 138)) > maxUint256 / 2n,
    tokenAddress: to,
    // Token approvals should not have value transfer or extra bytes.
    suspicious: BigInt(value) > 0n || data.length > 138,
  };
}

export const confirmButtonText = ({ method }: SignRequest) => {
  return method === 'eth_sendTransaction' ? 'Approve' : 'Sign';
};

export interface TxClassification {
  title: string;
  description: string;
  type: 'approval' | 'sign' | 'transaction';
  approval?: ApprovalInfo;
}

export function classifyTransaction(
  signRequest: SignRequest,
): TxClassification {
  // TODO: Classify all ERC20 operations (e.g. transfer)
  const approval = isApproval(signRequest);
  if (approval) {
    // early return on known requests.
    return {
      title: 'Token Approval',
      description:
        'You are approving a contract to spend your tokens. This transaction is required before the contract can transfer tokens on your behalf for swaps or other operations.',
      type: 'approval',
      approval,
    };
  }
  const method = signRequest.method;

  if (method === 'eth_sendTransaction') {
    return {
      title: 'Transaction',
      description: 'You are about to send a transaction on the blockchain.',
      type: 'transaction',
    };
  } else {
    const dataType = MESSAGE_METHODS.includes(method)
      ? 'a message'
      : 'typed data';
    return {
      title: 'Sign',
      description: `You are being asked to sign ${dataType}. This action does not cost gas or transfer any tokens.`,
      type: 'sign',
    };
  }
}
